import InlineErrorSpan from "components/shared/form/InlineErrorSpan";
import { Button } from "components/ui/Button";
import { TextInput } from "lib/miolo";
import checkCanSubmit from "lib/utils/form/checkCanSubmit";
import validateFormValuesAgainstSchema from "lib/utils/form/validateFormValuesAgainstSchema";
import Link from "next/link";
import type { ReactElement } from "react";
import { useField, useForm } from "react-final-form-hooks";
import { object, string } from "yup";

interface ILoginCredentialsFormValues {
  email: string;
  password: string;
}

interface ILoginCredentialsFormProps {
  isPromo?: boolean;
  onSubmit: (values: ILoginCredentialsFormValues) => void;
}

export default function LoginCredentialsForm({ isPromo, onSubmit }: ILoginCredentialsFormProps): ReactElement {
  const { form, handleSubmit, ...props } = useForm({
    onSubmit: (values: any) => onSubmit(values),
    validate: validateFormValuesAgainstSchema(loginCredentialsDataShape),
    initialValues: {
      email: "",
      password: "",
    },
  });

  const { submitting } = props;
  const canSubmit = checkCanSubmit(props);

  const email = useField("email", form);
  const password = useField("password", form);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col space-y-3 text-left">
        <TextInput
          {...email.input}
          height={44}
          type="email"
          spellCheck={false}
          autoComplete="off"
          placeholder="Email"
          name="email"
          maxLength={256}
        />

        {email.meta.touched && email.meta.error && <InlineErrorSpan>{email.meta.error}</InlineErrorSpan>}
      </div>

      <div className="flex flex-col space-y-3 text-left">
        <TextInput
          {...password.input}
          height={44}
          type="password"
          spellCheck={false}
          autoComplete="off"
          placeholder="Senha"
          name="password"
          maxLength={256}
        />

        {password.meta.touched && password.meta.error && <InlineErrorSpan>{password.meta.error}</InlineErrorSpan>}
      </div>

      {!isPromo && (
        <div className="text-right">
          <Link href="/forgot-password" className="inline-flex text-sm font-medium text-blue-600 hover:underline">
            Esqueceu sua senha?
          </Link>
        </div>
      )}

      <Button type="submit" variant="default" className="w-full" disabled={!canSubmit}>
        {submitting ? "Aguarde..." : "Logar"}
      </Button>
    </form>
  );
}

const loginCredentialsDataShape = object().shape({
  email: string().trim().required("Seu email é obrigatório").email("O email fornecido é inválido"),
  password: string().trim().required("Sua senha é obrigatória").min(8, "A senha deve ter no mínimo 8 dígitos"),
});
